import React, { useState } from 'react';

const GrammarUnit10Content = ({ unitData, userPreferences, onMarkComplete, userProgress }) => {
  const [currentSection, setCurrentSection] = useState('overview');

  // Safety check for unit data
  if (!unitData || !unitData.content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/30 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">No Content Available</h2>
            <p className="text-gray-300">Content for Unit 10 is not available.</p>
          </div>
        </div>
      </div>
    );
  }

  const { content } = unitData;

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">分詞概覽</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {content.overview.content.types.map((type, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{type.name}</h4>
              <p className="text-yellow-300 font-mono mb-2">{type.type}</p>
              <p className="text-gray-300 mb-2">{type.meaning}</p>
              <p className="text-blue-300 text-sm">{type.example}</p>
            </div>
          ))}
        </div>

        <div className="bg-gray-700/50 p-4 rounded-lg">
          <h4 className="text-lg font-semibold mb-3 text-green-300">基本動詞變化</h4>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-600">
              <thead>
                <tr className="bg-gray-600">
                  <th className="border border-gray-600 p-2 text-left">原形</th>
                  <th className="border border-gray-600 p-2 text-left">現在分詞</th>
                  <th className="border border-gray-600 p-2 text-left">過去分詞</th>
                </tr>
              </thead>
              <tbody>
                {content.overview.content.basicVerbs.map((verb, index) => (
                  <tr key={index} className="border-b border-gray-600">
                    <td className="border border-gray-600 p-2 text-blue-300">{verb.base}</td>
                    <td className="border border-gray-600 p-2 text-green-300">{verb.ving}</td>
                    <td className="border border-gray-600 p-2 text-yellow-300">{verb.pp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUsage = () => (
    <div className="space-y-6">
      {Object.entries(content).filter(([key]) => key.startsWith('usage')).map(([key, usageData]) => (
        <div key={key} className="bg-gray-800/30 rounded-xl p-6">
          <h3 className="text-xl font-bold mb-4 text-blue-300">{usageData.title}</h3>
          <p className="text-gray-300 mb-4">{usageData.content.description}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(usageData.content.comparison || {}).map(([type, data]) => (
              <div key={type} className="bg-gray-700/50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold mb-2 text-green-300">{type === 'ving' ? '現在分詞' : '過去分詞'}</h4>
                <p className="text-gray-300 mb-2">{data.function}</p>
                <p className="text-yellow-300 font-mono text-sm mb-2">{data.formation}</p>
                {data.withBe && <p className="text-blue-300 text-sm mb-2">{data.withBe}</p>}
                {data.withHave && <p className="text-blue-300 text-sm mb-2">{data.withHave}</p>}
                {data.example && <p className="text-gray-300 text-sm">{data.example}</p>}
                {data.examples && (
                  <div className="space-y-1 mt-2">
                    {data.examples.map((example, idx) => (
                      <p key={idx} className="text-gray-300 text-sm">{example}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderCompounds = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">複合形容詞的構成</h3>
        
        <div className="space-y-6">
          {Object.entries(content.compounds.content).map(([type, data]) => (
            <div key={type} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">{data.title}</h4>
              
              <div className="space-y-4">
                {data.patterns.map((pattern, index) => (
                  <div key={index} className="bg-gray-600/30 p-3 rounded">
                    <h5 className="text-md font-semibold mb-2 text-purple-300">{pattern.type}</h5>
                    <div className="space-y-2">
                      {pattern.examples.map((example, idx) => (
                        <div key={idx} className="text-sm">
                          <p className="text-blue-300">{example.compound}</p>
                          <p className="text-gray-400">{example.meaning}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMemoryTips = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">記憶技巧</h3>
        
        <div className="space-y-4">
          {content.memory_tips.tips.map((tip, index) => (
            <div key={index} className="bg-gray-700/50 p-3 rounded-lg">
              <p className="text-gray-300">{tip}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderExercises = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">練習題</h3>
        
        <div className="space-y-6">
          {content.exercises.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">{section.name}</h4>
              
              <div className="space-y-4">
                {section.questions.map((question, questionIndex) => (
                  <div key={questionIndex} className="bg-gray-600/30 p-3 rounded">
                    <p className="text-gray-300 mb-3">{question.question}</p>
                    <div className="space-y-2">
                      {Object.entries(question.options).map(([key, option]) => (
                        <div key={key} className="flex items-center">
                          <span className="text-yellow-300 font-mono mr-3">{key}.</span>
                          <span className="text-gray-300">{option}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderNavigation = () => (
    <div className="bg-gray-800/30 rounded-xl p-4 mb-6">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setCurrentSection('overview')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'overview'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          概覽
        </button>
        <button
          onClick={() => setCurrentSection('usage')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'usage'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          用法
        </button>
        <button
          onClick={() => setCurrentSection('compounds')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'compounds'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          複合形容詞
        </button>
        <button
          onClick={() => setCurrentSection('memory_tips')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'memory_tips'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          記憶技巧
        </button>
        <button
          onClick={() => setCurrentSection('exercises')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'exercises'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          練習題
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gray-800/30 rounded-xl p-6 mb-6">
          <h1 className="text-3xl font-bold mb-2 text-blue-300">
            {unitData.title[userPreferences?.language || 'traditional']}
          </h1>
          <p className="text-gray-300 mb-4">
            {unitData.subtitle[userPreferences?.language || 'traditional']}
          </p>
          <p className="text-gray-400">
            {unitData.description[userPreferences?.language || 'traditional']}
          </p>
        </div>

        {/* Navigation */}
        {renderNavigation()}

        {/* Content */}
        {currentSection === 'overview' && renderOverview()}
        {currentSection === 'usage' && renderUsage()}
        {currentSection === 'compounds' && renderCompounds()}
        {currentSection === 'memory_tips' && renderMemoryTips()}
        {currentSection === 'exercises' && renderExercises()}

        {/* Complete Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => onMarkComplete && onMarkComplete(unitData.id)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            標記為完成
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrammarUnit10Content;
